import { requireFiniteNonNegative } from "../shared/validation";

export interface Macronutrients {
  proteinGrams: number;
  /** Total carbohydrate. Fibre is represented within, and never added to, this total. */
  carbohydrateGrams: number;
  fatGrams: number;
}

export const NUTRIENT_UNITS = ["g", "mg", "mcg", "IU"] as const;
export type NutrientUnit = (typeof NUTRIENT_UNITS)[number];
export type MassNutrientUnit = Exclude<NutrientUnit, "IU">;

/** These limits are shared with backup validation so valid local data is always exportable. */
export const NUTRIENT_KEY_MAX_LENGTH = 100;
export const NUTRIENT_NAME_MAX_LENGTH = 200;

/**
 * Mass micronutrients are accumulated in micrograms. Using the smallest
 * supported unit keeps addition deterministic regardless of food order and
 * avoids throwing when providers use different, but compatible, mass units.
 */
export const CANONICAL_MASS_NUTRIENT_UNIT: MassNutrientUnit = "mcg";

const MICROGRAMS_PER_UNIT: Readonly<Record<MassNutrientUnit, number>> = Object.freeze({
  g: 1_000_000,
  mg: 1_000,
  mcg: 1,
});

export interface MicronutrientAmount {
  readonly amount: number;
  readonly unit: NutrientUnit;
  /** Preserves an arbitrary user/provider label independently of its stable key. */
  readonly name?: string;
}

/**
 * Nutrient keys are extensible (for example vitamin_a, thiamin_b1, b12, iron).
 * "Vitamin B" is intentionally not a single hard-coded nutrient because it is a family.
 */
export type Micronutrients = Readonly<Record<string, MicronutrientAmount>>;

export interface NutrientProfile {
  readonly macros: Macronutrients;
  readonly fibreGrams?: number;
  readonly micronutrients: Micronutrients;
}

/**
 * Normalizes common microgram spellings at external and UI boundaries. The
 * canonical spelling is deliberately plain ASCII so it survives backup and
 * provider round-trips consistently.
 */
export function normalizeNutrientUnit(input: unknown): NutrientUnit {
  if (typeof input !== "string") throw new Error("Nutrient unit is required");
  const compact = input.trim();
  if (compact === "IU" || compact.toLocaleLowerCase() === "iu") return "IU";
  const lower = compact.toLocaleLowerCase();
  if (lower === "g" || lower === "mg") return lower;
  if (lower === "mcg" || lower === "ug" || lower === "\u00b5g" || lower === "\u03bcg") return "mcg";
  throw new Error(`Unsupported nutrient unit: ${input}`);
}

export function isMassNutrientUnit(unit: NutrientUnit): unit is MassNutrientUnit {
  return unit !== "IU";
}

export function areNutrientUnitsCompatible(left: NutrientUnit, right: NutrientUnit): boolean {
  return left === right || (isMassNutrientUnit(left) && isMassNutrientUnit(right));
}

export function convertNutrientAmount(
  amount: number,
  fromUnit: NutrientUnit | string,
  toUnit: NutrientUnit | string,
): number {
  requireFiniteNonNegative(amount, "Nutrient amount");
  const from = normalizeNutrientUnit(fromUnit);
  const to = normalizeNutrientUnit(toUnit);
  if (from === to) return amount;
  if (!isMassNutrientUnit(from) || !isMassNutrientUnit(to)) {
    throw new Error(`Cannot convert nutrient amount from ${from} to ${to}`);
  }
  return (amount * MICROGRAMS_PER_UNIT[from]) / MICROGRAMS_PER_UNIT[to];
}

export function toCanonicalMicronutrientAmount(amount: MicronutrientAmount): MicronutrientAmount {
  requireFiniteNonNegative(amount.amount, "Nutrient amount");
  const unit = normalizeNutrientUnit(amount.unit);
  const name = amount.name?.trim() || undefined;
  return isMassNutrientUnit(unit)
    ? { amount: convertNutrientAmount(amount.amount, unit, CANONICAL_MASS_NUTRIENT_UNIT), unit: CANONICAL_MASS_NUTRIENT_UNIT, name }
    : { amount: amount.amount, unit, name };
}

/** Builds a stable key while retaining the original label on the amount. */
export function nutrientKeyFromName(name: string): string {
  const trimmedName = name.trim();
  if (!trimmedName) throw new Error("Micronutrient name cannot be empty");
  if (trimmedName.length > NUTRIENT_NAME_MAX_LENGTH) {
    throw new Error(`Micronutrient name cannot exceed ${NUTRIENT_NAME_MAX_LENGTH} characters`);
  }
  const key = trimmedName
    .normalize("NFKC")
    .toLocaleLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "");
  if (!key) throw new Error("Micronutrient name cannot be empty");
  if (key.length > NUTRIENT_KEY_MAX_LENGTH) {
    throw new Error(`Micronutrient key cannot exceed ${NUTRIENT_KEY_MAX_LENGTH} characters`);
  }
  return key;
}

/** Keeps non-convertible IU values in a separate aggregation dimension. */
export function nutrientKeyFromNameAndUnit(name: string, unit: NutrientUnit | string): string {
  const key = nutrientKeyFromName(name);
  const unitKey = normalizeNutrientUnit(unit) === "IU" && !key.endsWith("-iu") ? `${key}-iu` : key;
  if (unitKey.length > NUTRIENT_KEY_MAX_LENGTH) {
    throw new Error(`Micronutrient key cannot exceed ${NUTRIENT_KEY_MAX_LENGTH} characters`);
  }
  return unitKey;
}

export function validateNutrientProfile(profile: NutrientProfile): NutrientProfile {
  requireFiniteNonNegative(profile.macros.proteinGrams, "Protein");
  requireFiniteNonNegative(profile.macros.carbohydrateGrams, "Carbohydrate");
  requireFiniteNonNegative(profile.macros.fatGrams, "Fat");
  if (profile.fibreGrams !== undefined) {
    requireFiniteNonNegative(profile.fibreGrams, "Fibre");
    if (profile.fibreGrams > profile.macros.carbohydrateGrams) {
      throw new Error("Fibre cannot exceed total carbohydrate");
    }
  }
  const micronutrients: Record<string, MicronutrientAmount> = {};
  for (const [key, nutrient] of Object.entries(profile.micronutrients)) {
    if (!key.trim()) throw new Error("Micronutrient name cannot be empty");
    if (key.length > NUTRIENT_KEY_MAX_LENGTH) {
      throw new Error(`Micronutrient key cannot exceed ${NUTRIENT_KEY_MAX_LENGTH} characters`);
    }
    requireFiniteNonNegative(nutrient.amount, key);
    if (nutrient.name !== undefined && !nutrient.name.trim()) {
      throw new Error(`Display name for ${key} cannot be empty`);
    }
    if (nutrient.name !== undefined && nutrient.name.trim().length > NUTRIENT_NAME_MAX_LENGTH) {
      throw new Error(`Display name for ${key} cannot exceed ${NUTRIENT_NAME_MAX_LENGTH} characters`);
    }
    micronutrients[key] = {
      amount: nutrient.amount,
      unit: normalizeNutrientUnit(nutrient.unit),
      name: nutrient.name?.trim() || undefined,
    };
  }
  return {
    macros: { ...profile.macros },
    fibreGrams: profile.fibreGrams,
    micronutrients,
  };
}

export function scaleNutrients(profile: NutrientProfile, factor: number): NutrientProfile {
  requireFiniteNonNegative(factor, "Nutrition scale factor");
  return {
    macros: {
      proteinGrams: profile.macros.proteinGrams * factor,
      carbohydrateGrams: profile.macros.carbohydrateGrams * factor,
      fatGrams: profile.macros.fatGrams * factor,
    },
    fibreGrams: profile.fibreGrams === undefined ? undefined : profile.fibreGrams * factor,
    micronutrients: Object.fromEntries(
      Object.entries(profile.micronutrients).map(([name, value]) => [name, { ...value, amount: value.amount * factor }]),
    ),
  };
}

export function addNutrients(a: NutrientProfile, b: NutrientProfile): NutrientProfile {
  const names = new Set([...Object.keys(a.micronutrients), ...Object.keys(b.micronutrients)]);
  const micronutrients: Record<string, MicronutrientAmount> = {};
  for (const name of names) {
    const left = a.micronutrients[name];
    const right = b.micronutrients[name];
    if (left && right && !areNutrientUnitsCompatible(normalizeNutrientUnit(left.unit), normalizeNutrientUnit(right.unit))) {
      throw new Error(`Cannot add ${name} with incompatible units ${left.unit} and ${right.unit}`);
    }
    const canonicalLeft = left && toCanonicalMicronutrientAmount(left);
    const canonicalRight = right && toCanonicalMicronutrientAmount(right);
    const unit = canonicalLeft?.unit ?? canonicalRight?.unit;
    if (unit) {
      micronutrients[name] = {
        unit,
        amount: (canonicalLeft?.amount ?? 0) + (canonicalRight?.amount ?? 0),
        name: canonicalLeft?.name ?? canonicalRight?.name,
      };
    }
  }
  return {
    macros: {
      proteinGrams: a.macros.proteinGrams + b.macros.proteinGrams,
      carbohydrateGrams: a.macros.carbohydrateGrams + b.macros.carbohydrateGrams,
      fatGrams: a.macros.fatGrams + b.macros.fatGrams,
    },
    fibreGrams:
      a.fibreGrams === undefined && b.fibreGrams === undefined ? undefined : (a.fibreGrams ?? 0) + (b.fibreGrams ?? 0),
    micronutrients,
  };
}

export const EMPTY_NUTRIENTS: NutrientProfile = Object.freeze({
  macros: Object.freeze({ proteinGrams: 0, carbohydrateGrams: 0, fatGrams: 0 }),
  micronutrients: Object.freeze({}),
});
